import { motion } from "framer-motion";

// Chapter 02 — Quiénes somos · Manifiesto, composed with the same
// editorial rhythm as Disciplinas: chapter label → editorial statement
// → supporting body → structured grid of numbered principles → coda.
// No drop caps, no cards, no decoration — pure hierarchy.

const principles = [
  {
    n: "01",
    title: "No somos una agencia más.",
    body: "No vendemos plantillas ni promesas grandilocuentes. Diseñamos con criterio, cada decisión al servicio del proyecto.",
  },
  {
    n: "02",
    title: "No intentamos hacer de todo.",
    body: "Nos concentramos en landing pages editoriales para pequeños negocios. Una sola disciplina, refinada hasta el último detalle.",
    italic: true,
  },
  {
    n: "03",
    titleLead: "Solo hacemos una cosa.",
    titleTail: "Y la hacemos muy bien.",
    body: "Herramientas de venta silenciosas: rápidas, claras y pensadas para cerrar clientes mientras el negocio duerme.",
  },
];

export default function WhoWeAre() {
  const ease = [0.76, 0, 0.24, 1];

  return (
    <section
      id="studio"
      data-testid="who-we-are"
      className="relative py-16 lg:py-24"
    >
      <div className="max-w-[95%] mx-auto">
        {/* HEADER — mirrors the Disciplinas header composition */}
        <div className="flex items-start justify-between mb-12 lg:mb-20 gap-8">
          <div>
            <div className="font-sans text-[11px] tracking-[0.24em] uppercase text-[color:var(--ink-60)] mb-5">
              — Estudio · Manifiesto · Índice 002
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tightest max-w-3xl pb-2"
            >
              Un estudio.
              <br />
              <span className="font-italic-display">Una sola obsesión.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease, delay: 0.1 }}
              className="mt-6 font-sans text-base lg:text-lg leading-relaxed text-[color:var(--ink)]/80 max-w-xl"
            >
              Somos una agencia creativa boutique en Barcelona. Diseñamos landing pages para pequeños negocios que quieren que su primera impresión esté a la altura de su trabajo.
            </motion.p>
          </div>

          {/* Right annotation — same weight and slot as the Disciplinas note */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease, delay: 0.15 }}
            className="hidden lg:flex flex-col items-end text-right gap-4 max-w-[13rem]"
          >
            <div className="font-italic-display text-lg leading-[1.25] text-[color:var(--ink)]/75">
              Una obsesión.
              <br />
              Un método.
              <br />
              Un estudio.
            </div>
            <div className="w-10 h-px bg-[color:var(--ink)]/40" />
            <div className="font-sans text-[11px] tracking-[0.28em] uppercase text-[color:var(--ink-60)]">
              Barcelona · 2026
            </div>
          </motion.div>
        </div>

        {/* THREE NUMBERED PRINCIPLES — same rhythm as the five disciplinas.
            Vertical hairlines between columns, chapter marker on top, editorial
            statement in the middle, short body underneath. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12 border-t border-[color:var(--ink-10)] pt-10">
          {principles.map((p, i) => (
            <motion.div
              key={p.n}
              data-testid={`manifesto-principle-${p.n}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease, delay: i * 0.1 }}
              className="group md:border-l md:border-[color:var(--ink-10)] md:pl-6 first:md:border-l-0 first:md:pl-0"
            >
              {/* Chapter marker — index + rule, echoes the site's chapter system */}
              <div className="flex items-center gap-3 mb-6">
                <span className="font-sans text-[12px] tracking-[0.28em] uppercase text-[color:var(--ink-60)]">
                  {p.n}
                </span>
                <span className="h-px flex-1 bg-[color:var(--ink-10)] group-hover:bg-[color:var(--ink)]/60 transition-colors duration-500" />
              </div>

              {/* Editorial statement — sized to breathe without dominating */}
              <h3 className="font-display text-2xl lg:text-[1.7rem] leading-[1.15] tighter pb-1 mb-4">
                {p.titleLead ? (
                  <>
                    {p.titleLead}
                    <br />
                    <span className="font-italic-display" style={{ color: "#FF6B2C" }}>
                      {p.titleTail}
                    </span>
                  </>
                ) : p.italic ? (
                  <span className="font-italic-display">{p.title}</span>
                ) : (
                  p.title
                )}
              </h3>

              {/* Supporting body — small, calm */}
              <p className="font-sans text-sm leading-relaxed text-[color:var(--ink)]/75 max-w-sm">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CODA — closing editorial note, right-aligned to mirror the header's
            annotation slot and bookend the composition */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease, delay: 0.2 }}
          className="mt-16 lg:mt-24 pt-10 border-t border-[color:var(--ink-10)] grid grid-cols-1 md:grid-cols-12 gap-6 items-baseline"
        >
          <div className="md:col-span-3 font-sans text-[11px] tracking-[0.24em] uppercase text-[color:var(--ink-60)]">
            — Coda
          </div>
          <p className="md:col-span-6 font-display text-2xl lg:text-3xl leading-[1.2] tighter">
            Webs bonitas.{" "}
            <span className="text-[color:var(--ink)]/60">Sin complicaciones.</span>{" "}
            <span className="font-italic-display" style={{ color: "#FF6B2C" }}>
              Sin agencias gigantes.
            </span>
          </p>
          <div className="md:col-span-3 md:text-right font-sans text-[11px] tracking-[0.24em] uppercase text-[color:var(--ink-60)]">
            Continúa · Disciplinas
          </div>
        </motion.div>
      </div>
    </section>
  );
}
